import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { env } from '@/config/env';
import { api, getClientIp } from '@/api/client';
import { tokenStore } from '@/utils/tokenStore';
import { mergeWatermarkConfig, type WatermarkConfig } from './watermark-config';
import { watermarkSessionId, type WatermarkTokens } from './watermark-tile';
import { mountScreenWatermark } from './watermark-host';
import type { WatermarkAdapter } from './watermark-adapter';
import { appIdForPath } from './apps';

const BANK_NAME = 'AndHub';
// Routes where the mark should never show (unauthenticated / kiosk surfaces, and the
// watermark settings page which renders its own live preview from the unsaved config).
const BLOCKED_PREFIXES = ['/login', '/callback', '/intro', '/welcome', '/pallet-ross-landing', '/admin/settings'];

/**
 * Mounts the hardened, admin-configurable screen watermark across the whole
 * AndHub platform. Renders nothing itself — it drives the framework-free host.
 * Must live inside AuthProvider + a Router (uses useAuth + useLocation).
 */
export function ScreenWatermark(): null {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();

  const { data: config } = useQuery({
    queryKey: ['watermark-config'],
    queryFn: () => api.get<Partial<WatermarkConfig>>('/api/settings/watermark').then(mergeWatermarkConfig),
    enabled: isAuthenticated,
    // Hot-path security config: keep clients close to the server. The read is
    // Redis-cached server-side, so refetching often is cheap. Poll every 60s and
    // on tab focus/navigation so an admin's change propagates to everyone within
    // ~a minute without a reload (no WebSocket push at this scale).
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // Client IP for the optional ipAddress token — fetched once when signed in.
  const ipRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated) { ipRef.current = null; return; }
    let alive = true;
    getClientIp().then((ip) => { if (alive) ipRef.current = ip; });
    return () => { alive = false; };
  }, [isAuthenticated]);

  // Latest values exposed to the adapter closure via refs so update() always reads fresh state.
  const configRef = useRef<WatermarkConfig | null>(null);
  const tokensRef = useRef<WatermarkTokens | null>(null);
  configRef.current = config ?? null;
  tokensRef.current = user
    ? {
        bankName: BANK_NAME,
        userName: user.msDisplayName ?? user.name ?? '',
        employeeId: user.employeeId ?? '',
        userEmail: user.email ?? '',
        sessionId: watermarkSessionId(),
        ipAddress: ipRef.current ?? undefined,
      }
    : null;

  const ctlRef = useRef<ReturnType<typeof mountScreenWatermark> | null>(null);

  // Mount the host once.
  useEffect(() => {
    const adapter: WatermarkAdapter = {
      apiBaseUrl: env.apiUrl,
      tamperPath: '/api/track/watermark-tamper',
      getConfig: () => configRef.current,
      getTokens: () => tokensRef.current,
      getAuthHeaders: () => {
        const t = tokenStore.getAccessToken();
        return t ? { Authorization: `Bearer ${t}` } : {};
      },
      isRouteAllowed: (path) => {
        if (BLOCKED_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))) return false;
        // Scope: empty apps = entire platform; otherwise only the selected apps.
        const apps = configRef.current?.apps ?? [];
        if (apps.length === 0) return true;
        const id = appIdForPath(path);
        return id != null && apps.includes(id);
      },
    };
    const ctl = mountScreenWatermark(adapter);
    ctlRef.current = ctl;
    return () => { ctl.destroy(); ctlRef.current = null; };
  }, []);

  // Re-evaluate whenever config, user, or route changes.
  useEffect(() => { ctlRef.current?.update(); }, [config, user, isAuthenticated, pathname]);

  return null;
}
